import Vue from 'vue';
import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import { ShiftsListPageQuery } from './queries/_build_documents_list';

import { fetchEnumsFromSchema } from '@/core/tools/forms/fetch_enums_from_schema';


interface ShiftsListState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;

    lastMeta: {
        page: number;
        pageSize: number;
        totalCount: number;
        totalPages: number;
    };

    documents: any[];
    previewShiftProducts: any[];

    stats: any;
    meta: any;
}

const state: ShiftsListState = {
    loading: false,
    loaded: false,
    fatalError: false,

    lastMeta: {
        page: 0,
        pageSize: 0,
        totalCount: 0,
        totalPages: 0
    },

    documents: [],
    previewShiftProducts: [],

    stats: null,

    meta: {}
};

function relatedProductsQuery(dealId: string) {
    return {
      columns: {
        id: 'basket_item.id',
        container_quantity: 'basket_item.container_quantity',
        container_price: 'basket_item.container_price',
        notes: 'basket_item.notes',
        created_at: 'basket_item.created_at',
        updated_at: 'basket_item.updated_at',
        product_container_id: 'variant.id',
        product_container_name: 'variant.name',
        product_container_price: 'variant.price',
        product_id: 'product.id',
        product_name: 'product.name',
        product_is_sample: 'variant.is_sample',
        item_is_sample: 'basket_item.is_sample',
      },

      from: 's:deal_product_containers',
      from_as: 'basket_item',
      joins: [
        {
          join: 's:product_containers',
          type: 'left',
          as:   'variant',
          on: {
            $eq: ['basket_item.product_container_id', 'variant.id']
          }
        },
        {
          join: 's:products',
          type: 'left',
          as:   'product',
          on: {
            $eq: ['variant.product_id', 'product.id']
          }
        }
      ],
      order: [
        {$desc: ['basket_item.id']}
      ],
      where: {
        $eq: [`basket_item.deal_id`, {$: dealId}]
      }
    };
}

export const ITEMS_LIST = 'ITEMS_LIST';
export const STATS_FOR_PAYMENTS = 'STATS_FOR_PAYMENTS';
export const STATS_FOR_PRODUCTS = 'STATS_FOR_PRODUCTS';
export const STATS_COMMON = 'STATS_COMMON';
export const LOAD_RELATED_DEAL_QUERY = 'LOAD_RELATED_DEAL_QUERY';
export const PL_RELAED_PRODUCTS_LIST = 'PL_RELAED_PRODUCTS_LIST';
export const FACT_BLANK_ATOM_FACTS = 'FACT_BLANK_ATOM_FACTS';
export const FACT_BLANK_CUSTOM_FACTS = 'FACT_BLANK_CUSTOM_FACTS';

/** Факты АТОМ по списку смен (s:shift_atom + s:shift_fact_atom) */
function factBlankAtomQuery(shiftIds: string[]) {
    return {
        columns: {
            shift_id: 'sa.shift_id',
            number: 'sa.number',
            product_name: {$concat: ['p.name', {$: " "}, 'pc.name']},
            fact_container_name: 'vcf.name',
            container_name: 'vc.name',
            // Кол-во выпущенного продукта, шт — зарегистрированный факт,
            // а не плановое значение партии (sa.quantity)
            fact_quantity: 'sfa.quantity',
        },
        from: 's:shift_atom',
        from_as: 'sa',
        joins: [
            {
                join: 's:shift_fact_atom',
                type: 'left',
                as: 'sfa',
                on: {
                    $eq: ['sfa.plan_id', 'sa.id']
                }
            },
            {
                join: 's:products',
                type: 'left',
                as: 'p',
                on: {
                    $eq: ['sa.product_id', 'p.id']
                }
            },
            {
                join: 's:product_containers',
                type: 'left',
                as: 'pc',
                on: {
                    $eq: ['sa.product_container_id', 'pc.id']
                }
            },
            {
                join: 's:voc_containers',
                type: 'left',
                as: 'vc',
                on: {
                    $eq: ['sa.container_id', 'vc.id']
                }
            },
            {
                join: 's:voc_containers',
                type: 'left',
                as: 'vcf',
                on: {
                    $eq: ['sfa.container_id', 'vcf.id']
                }
            },
        ],
        order: [
            {$asc: ['sa.number']}
        ],
        page_size: 10000,
        where: {
            $in: ['sa.shift_id', {$: shiftIds}]
        }
    };
}

/** Факты Общей линии по списку смен (готовая продукция + полуфабрикат) */
function factBlankCustomQuery(shiftIds: string[]) {
    return {
        columns: {
            shift_id: 'scp.shift_id',
            number: 'scp.number',
            kind: 'scp.kind',
            product_name: 'p.name',
            container_name: 'pc.name',
            // Кол-во выпущенного продукта, шт — зарегистрированный факт
            // (у плана Общей линии количества нет вообще)
            fact_quantity: 'scf.quantity',
        },
        from: 's:shift_custom_plan',
        from_as: 'scp',
        joins: [
            {
                join: 's:shift_custom_fact',
                type: 'left',
                as: 'scf',
                on: {
                    $eq: ['scf.plan_id', 'scp.id']
                }
            },
            {
                join: 's:products',
                type: 'left',
                as: 'p',
                on: {
                    $eq: ['scp.product_id', 'p.id']
                }
            },
            {
                join: 's:product_containers',
                type: 'left',
                as: 'pc',
                on: {
                    $eq: ['scf.product_container_id', 'pc.id']
                }
            },
        ],
        order: [
            {$asc: ['scp.number']}
        ],
        page_size: 10000,
        where: {
            $and: [
                {$in: ['scp.shift_id', {$: shiftIds}]},
                {$in: ['scp.kind', {$: ['finished', 'semi']}]},
            ]
        }
    };
}

export const actions: ActionTree<ShiftsListState, RootState> = {

    loadData({ commit, getters, dispatch }, query) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(ITEMS_LIST)
                    .raw('documents.list')
                    .action(ShiftsListPageQuery.build(query))
            ]).then(((result: ServerResult) => {
                commit('SET_ITEMS_LIST', result.getOperation(ITEMS_LIST).getContent());
                commit('LOADED_SUCCESS');

                resolve();
            }));
        })
    },

    exportDataForListExport({ commit, getters, dispatch }, query) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            const action = ShiftsListPageQuery.build(query)
            action.page_size = 10000
            action.page = 1

            vm.$db([
                vm.$q(ITEMS_LIST)
                    .raw('documents.list')
                    .action(action)
            ]).then(((result: ServerResult) => {
                resolve(result.getOperation(ITEMS_LIST).getContent());
            }));
        })
    },

    /**
     * Пакетная загрузка фактов для экспорта бланка факта из списка смен.
     * Берутся только линии АТОМ ('atom') и Общая ('custom'); ВДП ('vdp') и мойки
     * игнорируются. Возвращает { atom: rows, custom: rows, skippedAll }.
     */
    exportFactBlanks({ commit, state }, { shifts }) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            const atomIds: string[] = (shifts || [])
                .filter((shift: any) => shift.template === 'atom')
                .map((shift: any) => shift.id);
            const customIds: string[] = (shifts || [])
                .filter((shift: any) => shift.template === 'custom')
                .map((shift: any) => shift.id);

            if (process.env.NODE_ENV === 'development') {
                console.debug('[ShiftsList] exportFactBlanks: atom=', atomIds.length,
                    ', custom=', customIds.length,
                    ', ignored=', (shifts || []).length - atomIds.length - customIds.length);
            }

            if (atomIds.length === 0 && customIds.length === 0) {
                resolve({ atom: [], custom: [], skippedAll: true });
                return;
            }

            const queries: any[] = [];
            if (atomIds.length > 0) {
                queries.push(
                    vm.$q(FACT_BLANK_ATOM_FACTS)
                        .raw('documents.list')
                        .action(factBlankAtomQuery(atomIds))
                );
            }
            if (customIds.length > 0) {
                queries.push(
                    vm.$q(FACT_BLANK_CUSTOM_FACTS)
                        .raw('documents.list')
                        .action(factBlankCustomQuery(customIds))
                );
            }

            vm.$db(queries).then((result: ServerResult) => {
                const atom: any[] = atomIds.length > 0
                    ? result.getOperation(FACT_BLANK_ATOM_FACTS).getContent().documents
                    : [];
                const custom: any[] = customIds.length > 0
                    ? result.getOperation(FACT_BLANK_CUSTOM_FACTS).getContent().documents
                    : [];

                if (process.env.NODE_ENV === 'development') {
                    console.debug('[ShiftsList] exportFactBlanks loaded: atom rows=', atom.length,
                        ', custom rows=', custom.length);
                }

                resolve({ atom, custom, skippedAll: false });
            });
        })
    },

    setStatus({commit}, documents) {
        commit('START_LOADING');
        const vm = (this as any)._vm;
        const _ids: any = [];
        Promise.all(
            documents.map(
                (document: any) => {
                    return new Promise((resolve, reject) => {
                        vm.$db([
                            (vm.$q('DEAL_TO_DONE'))
                            .update(document.id, { status_id: 'd:statuses:18' })
                        ])
                        .then((result: ServerResult) => {
                            // commit('SET_ITEM_LIST', result.getOperation(PL_RELAED_PRODUCTS_LIST).getContent());
                            _ids.push(document.id)
                            resolve();
                        })
                    })
                }
            )
        ).then(() => {
            vm.$db([
                vm.$q(ITEMS_LIST)
                    .raw('documents.list')
                    .action(ShiftsListPageQuery.build({dealId: _ids}))
            ]).then(((result: ServerResult) => {
                commit('UPDATE_ITEMS_LIST', result.getOperation(ITEMS_LIST).getContent());
                commit('LOADED_SUCCESS');
            }));
        })
    },

    copyShift({rootState}, $document) {
        const vm = (this as any)._vm;

        console.log('document', $document);

        return new Promise((resolve, reject) => {
            vm.$db([
                vm.$q(LOAD_RELATED_DEAL_QUERY)
                .raw('documents.get')
                .action({ id: $document.id }),

              vm.$q(PL_RELAED_PRODUCTS_LIST)
                .raw('documents.list')
                .action(relatedProductsQuery($document.id)),

            ])
            .then((result: ServerResult) => {
              const dealResult = result.getOperation(LOAD_RELATED_DEAL_QUERY).getContent().document;
              const dealBasketResult = result.getOperation(PL_RELAED_PRODUCTS_LIST).getContent().documents;
              console.log('dealResult, dealBasketResult', dealResult, dealBasketResult)
              // const opResult = result.getOperation(UNLINK_EMPLOYEE_OP);
              const allowedItemFields = ['deal_id', 'container_quantity', 'container_price', 'product_container_id', 'notes'];

              vm.$db([
                vm.$q('COPY_DEAL').create('deals', {
                  client_id: dealResult.client_id,
                  counterparty_id: dealResult.counterparty_id,
                  delivery_point_id: dealResult.delivery_point_id,
                  delivery_warehouse_id: dealResult.delivery_warehouse_id,
                  manager_id: dealResult.manager_id,
                  name: dealResult.name,
                  payment_type_id: dealResult.payment_type_id,
                  status_id: 'd:statuses:13'
                }).withManyRelation('deal_product_containers', 'deal_id', (createRelated: any) => {
                  return dealBasketResult.map((item: any, index: number) => {
                    const cleanDocuemnt: any = {
                      container_price: item.container_price,
                      container_quantity: item.container_quantity,
                      product_container_id: item.product_container_id,
                      notes: item.notes,
                    };

                    return createRelated(cleanDocuemnt);
                  })
                })
              ]).then((result: ServerResult) => {
                const opResult = result.getOperation('COPY_DEAL');

                if(!opResult.hasError()) {
                    resolve();
                } else {
                    reject();
                }
              })
            })
        });
      }
//   relatedShiftQuery(db: DataBaseService, dealId: string) {
//     return db.getById(LOAD_RELATED_DEAL_QUERY, dealId);
//   }

//   copyShift = ($document) => {
//     console.log('document', $document);

//     this.db.query([
//       this.relatedProductsQuery(this.db, $document.id),
//       this.relatedShiftQuery(this.db, $document.id)
//     ])
//     .toPromise()
//     .then((result: ServerResult) => {
//       const dealResult = result.getOperation(LOAD_RELATED_DEAL_QUERY).getContent().document;
//       const dealBasketResult = result.getOperation(PL_RELAED_PRODUCTS_LIST).getContent().documents;
//       console.log('dealResult, dealBasketResult', dealResult, dealBasketResult)
//       // const opResult = result.getOperation(UNLINK_EMPLOYEE_OP);
//       const allowedItemFields = ['deal_id', 'container_quantity', 'container_price', 'product_container_id', 'notes'];

//       this.db.query([
//         this.dbq.query('COPY_DEAL').create('deals', {
//           client_id: dealResult.client_id,
//           counterparty_id: dealResult.counterparty_id,
//           delivery_point_id: dealResult.delivery_point_id,
//           delivery_warehouse_id: dealResult.delivery_warehouse_id,
//           manager_id: dealResult.manager_id,
//           name: dealResult.name,
//           payment_type_id: dealResult.payment_type_id,
//           status_id: 'd:statuses:13'
//         }).withManyRelation('deal_product_containers', 'deal_id', (createRelated) => {
//           return dealBasketResult.map((item, index) => {
//             const cleanDocuemnt: any = {
//               container_price: item.container_price,
//               container_quantity: item.container_quantity,
//               product_container_id: item.product_container_id,
//               notes: item.notes,
//             };

//             return createRelated(cleanDocuemnt);
//           })
//         })
//       ]).toPromise().then((result: ServerResult) => {
//         const opResult = result.getOperation('COPY_DEAL');

//         if(!opResult.hasError()) {
//           this.table.reloadData()
//           this._snackBar.open('Заявка успешно скопированна', null, {
//             duration: 4000,
//           });
//         } else {
//           this._snackBar.open('Во время копирования возникла ошибка. Попробуйте позже', null, {
//             duration: 4000,
//           });
//         }
//       })
//     })
//   }

};

export const mutations: MutationTree<ShiftsListState> = {
    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },
    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },
    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },

    SET_ITEMS_LIST(state, data) {
        state.documents = data.documents.map((doc: any) => {
          doc.isNew = (doc.status_id === 'd:statuses:13');
          return doc;
        });

        state.lastMeta = {
            page: data.meta.page,
            pageSize: data.meta.page_size,
            totalCount: data.meta.total_count,
            totalPages: data.meta.total_pages
        };
        state.meta = fetchEnumsFromSchema(data.schema);
    },

    SET_SELECTED_DEAL_PRODUCTS(state, data) {
        state.previewShiftProducts = data.documents
    },

    UPDATE_ITEMS_LIST(state, data) {
        const _new_docs: any = {}
        data.documents.forEach((doc: any) => {
            _new_docs[doc.id] = doc;
        });
        const docs = state.documents.map((doc: any) => {
            if (_new_docs[doc.id] != undefined ) {
                doc = _new_docs[doc.id];
            }
            return doc;
        });
        state.documents = docs;
    },

    SET_STATS_DATA(state, data) {
        state.stats = data;
    },
};

const getters = {

};

export const STORE_KEY = '$_shifts_list';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
